import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthToken } from "../components/AuthWrapper";
import { useQueryClient } from "@tanstack/react-query";
import { setChessUsername } from "../utils/apiConfig";
import { useUserProfile } from "./api/useApiHooks";
import { queryKeys } from "../utils/queryKeys";
import type { UserProfile } from "../types/user";
import useSettingsStore from "../stores/useSettingsStore";
import type { CoachLevel } from "../stores/useSettingsStore";
import type { LinkPrompt } from "../features/onboarding/linkPrompt";
// localStorage cache — instant username on page load, no Clerk dependency. Shared with the
// game-import color resolver (utils/linkedUsername) so the key + shape have ONE definition.
import { readLinkedUsername as readCache, writeLinkedUsername as writeCache, clearLinkedUsername as clearCache } from "../utils/linkedUsername";

const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

/**
 * Hook to manage the user's linked chess username.
 *
 * Uses a two-layer strategy:
 *   1. localStorage cache — read instantly on mount, no network dependency.
 *      Pages like Library can render the correct state immediately.
 *   2. Background API fetch — verifies/updates the cache once Clerk auth is ready.
 *      Updates state if the server disagrees with the cache.
 */
export default function useChessUsername() {
  const { isSignedIn, isAuthReady, getAuthHeaders } = useAuthToken();

  // Seed state from localStorage cache — instant, no network
  const cached = useRef(readCache()).current;
  const [chessUsername, setChessUsernameState] = useState<string | null>(cached?.chessUsername ?? null);
  const [platform, setPlatform] = useState<string | null>(cached?.platform ?? null);
  const [fetchDone, setFetchDone] = useState(!!cached);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [forceShow, setForceShow] = useState(false);

  // Verify/update the cached link once signed in, from the profile (GET /users/me), which carries
  // chessUsername + chessPlatform from the same users row GET /usernames reads. The nav already fetches the
  // profile on every page, so this rides on that request instead of making its own (perf survey #10).
  const profile = useUserProfile();
  const queryClient = useQueryClient();
  const linked = profile.data?.chessUsername;
  const linkedPlatform = profile.data?.chessPlatform;
  useEffect(() => {
    if (!isSignedIn) return;
    if (profile.isSuccess) {
      if (linked) {
        setChessUsernameState(linked);
        setPlatform(linkedPlatform || null);
        writeCache(linked, linkedPlatform || "chess.com");
      } else {
        // Server confirmed: no linked username
        setChessUsernameState(null);
        setPlatform(null);
        clearCache();
      }
      setFetchFailed(false);
      setFetchDone(true);
    } else if (profile.isError) {
      // Auth/network error — keep cached state, don't overwrite with null
      setFetchFailed(true);
      setFetchDone(true);
    }
  }, [isSignedIn, profile.isSuccess, profile.isError, linked, linkedPlatform]);

  // Reset when signing out. Only once auth has resolved: before Clerk loads, isSignedIn reads false for
  // everyone, and resetting then wiped the cached link on every page load, so the "instant username" never
  // survived to the first useful render (and the home's pre-sign-in paint never saw a linked player).
  useEffect(() => {
    if (isAuthReady && !isSignedIn) {
      setChessUsernameState(null);
      setPlatform(null);
      clearCache();
      setFetchDone(false);
      setFetchFailed(false);
      setDismissed(false);
      setForceShow(false);
    }
  }, [isAuthReady, isSignedIn]);

  const isLinked = !!chessUsername;

  // `requested` when forced open via requestPrompt (the change/link flow, works even when linked); `auto` when the
  // fetch succeeded, the user is signed in, not linked, and hasn't dismissed it. Never `auto` if the fetch failed
  // (auth error): "not linked" must be confirmed, not assumed. Where each one shows: linkModalVisible.
  const linkPrompt: LinkPrompt = forceShow
    ? "requested"
    : CLERK_ENABLED && isSignedIn && fetchDone && !fetchFailed && !isLinked && !dismissed
      ? "auto"
      : "none";

  const linkUsername = useCallback(async (username: string, plat: string) => {
    try {
      const data = await setChessUsername(username, plat, getAuthHeaders) as {
        success?: boolean;
        chessUsername?: string;
        platform?: string;
        rating?: number | null;
        coachLevel?: string | null;
      };
      if (data.success) {
        setChessUsernameState(data.chessUsername || null);
        setPlatform(data.platform || null);
        if (data.chessUsername) {
          writeCache(data.chessUsername, data.platform || "chess.com");
        }
        // Keep the profile cache in step, or a stale profile would read "not linked" and undo this.
        queryClient.setQueryData<UserProfile>(queryKeys.userProfile, (p) => (p ? { ...p, chessUsername: data.chessUsername, chessPlatform: data.platform } : p));
        // Sync coach level from server recommendation to local settings
        if (data.coachLevel && (data.coachLevel === 'beginner' || data.coachLevel === 'advanced')) {
          useSettingsStore.getState().setCoachLevel(data.coachLevel as CoachLevel);
        }
        return { success: true, rating: data.rating ?? null, coachLevel: data.coachLevel ?? null };
      }
      return { error: "Unexpected response" };
    } catch (err: unknown) {
      const errObj = err as { data?: { error?: unknown }; message?: string };
      const rawError = errObj.data?.error;
      const errorMsg = typeof rawError === 'object' && rawError !== null
        ? (rawError as { message?: string }).message || JSON.stringify(rawError)
        : typeof rawError === 'string' ? rawError : undefined;
      return { error: errorMsg || errObj.message || "Failed to link username" };
    }
  }, [getAuthHeaders, queryClient]);

  const unlinkUsername = useCallback(() => {
    setChessUsernameState(null);
    setPlatform(null);
    clearCache();
    queryClient.setQueryData<UserProfile>(queryKeys.userProfile, (p) => (p ? { ...p, chessUsername: undefined, chessPlatform: undefined } : p));
  }, [queryClient]);

  const dismissPrompt = useCallback(() => {
    setDismissed(true);
    setForceShow(false);
  }, []);

  const requestPrompt = useCallback(() => {
    setForceShow(true);
  }, []);

  return {
    chessUsername,
    platform,
    isLoading: !fetchDone && !isLinked && CLERK_ENABLED && !!isSignedIn,
    isLinked,
    linkPrompt,
    linkUsername,
    unlinkUsername,
    dismissPrompt,
    requestPrompt,
  };
}